import React, { useState, MouseEvent } from 'react';
import { Card, palettes, EllipsisLoader, IconButton, useFlashMessage } from '@indigo-ag/component-module';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faFileDownload, faFileAlt } from '@fortawesome/pro-regular-svg-icons';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';

import { AgFinancingFile } from '@indigo-ag/schema';

import { createStylesHook } from 'shared/hooks/createStylesHook';
import { downloadFile } from 'shared/utils';

const useStyles = createStylesHook(({ typography: { pxToRem } }) => ({
  documentCard: {
    padding: `0 ${pxToRem(12)}`,
    marginBottom: pxToRem(12),
    height: pxToRem(40),
    width: 'min-content',
    maxWidth: '100%',
    '& button': {
      height: pxToRem(25),
      width: pxToRem(25),
      display: 'inline',
      '&:hover, &:focus': {
        transform: 'scale(1.1)',
        transition: '.2s'
      }
    }
  },
  documentName: {
    fontSize: pxToRem(13),
    color: palettes.brand.gray['700'],
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  documentIconBackground: {
    backgroundColor: palettes.brand.blue.base,
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    marginRight: '11px'
  },
  documentIcon: {
    color: palettes.brand.gray['100'],
    margin: '2px 5px',
    fontSize: pxToRem(12.8)
  }
}));

export interface DocumentCardProps {
  document: AgFinancingFile;
  classes?: {
    documentCard?: string;
  };
  onDelete?: () => void;
  additionalButtonSlot?: React.ReactNode;
}

export const DocumentCard: React.FunctionComponent<DocumentCardProps> = ({
  document,
  onDelete,
  classes,
  additionalButtonSlot
}) => {
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const { addFlashMessage } = useFlashMessage();

  const handleDownloadDocument = (event: MouseEvent<SVGSVGElement, globalThis.MouseEvent>) => {
    event.preventDefault();

    setIsDownloading(true);
    downloadFile(document)
      .catch((error: Error) =>
        addFlashMessage({
          content: error.message,
          title: '',
          variant: 'error'
        })
      )
      .finally(() => setIsDownloading(false));
  };

  const internalOnDelete = (event: MouseEvent<SVGSVGElement, globalThis.MouseEvent>) => {
    if (onDelete) {
      event.preventDefault();
      onDelete();
    }
  };

  const renderDeleteButton = () => {
    if (!onDelete) {
      return null;
    }

    return (
      <div data-testid="delete-container">
        <IconButton icon={faTrash} colorVariant="destructive" onClick={internalOnDelete} />
      </div>
    );
  };
  /**
   * Styles
   */
  const innerClasses = useStyles();

  return (
    <Card className={`${innerClasses.documentCard} flex align-items-center ${classes?.documentCard ?? ''}`}>
      <div className={innerClasses.documentIconBackground}>
        <FontAwesomeIcon icon={faFileAlt} className={innerClasses.documentIcon} />
      </div>
      <a className={innerClasses.documentName} href={document.documentUrl} target="_blank" rel="noopener noreferrer">
        <span title={document.documentName}>{document.documentName}</span>
      </a>

      <div className="flex">
        {isDownloading ? (
          <EllipsisLoader color={palettes.brand.blue.base} style={{ marginLeft: 12, minWidth: 22 }} />
        ) : (
          <div data-testid="download-container">
            <IconButton icon={faFileDownload} onClick={handleDownloadDocument} />
          </div>
        )}
        {renderDeleteButton()}
      </div>
      {additionalButtonSlot ? <div className="flex">{additionalButtonSlot}</div> : null}
    </Card>
  );
};
